import React from 'react';

import { renderToStaticMarkup } from 'react-dom/server';

import { findSerializedMentionRanges, MENTION_BOUNDARY_MARKER } from '../utils/inlineMentionRanges';

import type { ISelectedKnowledgeTagData } from '../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import type { ISelectedSkillTagData } from '../SkillMention/SelectedSkillTag';

import MentionToken from './MentionToken';

import type { TMentionEditorKind } from './types';
import { detectMentionScript } from '../mentionShared/mentionText';



export interface IMentionTextSegment {

  type: 'text';

  text: string;

}



export interface IMentionTokenSegment {

  type: 'mention';

  kind: TMentionEditorKind;

  body: string;

  mentionKey: string;

}



export type TMentionContentSegment = IMentionTextSegment | IMentionTokenSegment;



/** 将提交/历史用的纯文本解析为文本段 + mention 段（紧凑 `@body` / `/body`） */

export function parseSerializedMentions(value: string): TMentionContentSegment[] {

  if (!value) return [];



  const ranges = findSerializedMentionRanges(value).sort((a, b) => a.start - b.start);

  if (!ranges.length) return [{ type: 'text', text: value }];



  const segments: TMentionContentSegment[] = [];

  let cursor = 0;



  ranges.forEach((range) => {

    if (range.start > cursor) {

      segments.push({ type: 'text', text: value.slice(cursor, range.start) });

    }

    const kind = range.kind as TMentionEditorKind;

    const body = parseMentionBodyFromLabel(range.label, kind);

    if (body) {

      segments.push({

        type: 'mention',

        kind,

        body,

        mentionKey: range.key,

      });

    }

    cursor = range.end;

  });



  if (cursor < value.length) {

    segments.push({ type: 'text', text: value.slice(cursor) });

  }



  return segments;

}

/**
 * 规范化 mention 与相邻正文/mention 之间的可读空格。
 * 边界仍靠 MENTION_BOUNDARY_MARKER；空格仅用于展示与提交 payload，不参与边界解析。
 */
export function normalizeSerializedMentionGaps(value: string): string {
  if (!value) return '';

  const segments = parseSerializedMentions(value);
  let result = '';

  segments.forEach((segment) => {
    if (segment.type === 'text') {
      const { text } = segment;
      if (!text) return;
      if (
        result.length
        && result.endsWith(MENTION_BOUNDARY_MARKER)
        && !/^\s/.test(text)
      ) {
        result += ' ';
      }
      result += text;
      return;
    }

    const serialized = serializeMentionBody(segment.body, segment.kind);
    if (!serialized) return;
    if (result.length && !/\s$/.test(result)) {
      result += ' ';
    }
    result += serialized;
  });

  return result;
}

/** 将 MentionToken 组件渲染为可插入 contentEditable 的原子 DOM 节点 */

export function createMentionElement(

  body: string,

  kind: TMentionEditorKind,

  mentionKey: string,

  knowledgeItems?: ISelectedKnowledgeTagData[],

  skillPayload?: ISelectedSkillTagData,

): HTMLSpanElement {

  const normalizedBody = parseMentionBodyFromLabel(body, kind).trim();

  const script = kind === 'skill' ? detectMentionScript(normalizedBody) : undefined;

  const html = renderToStaticMarkup(

    <MentionToken
      kind={kind}
      body={normalizedBody}
      mentionKey={mentionKey}
      script={script}
    />,

  );

  const tpl = document.createElement('template');

  tpl.innerHTML = html.trim();

  const el = tpl.content.firstChild as HTMLSpanElement;

  if (knowledgeItems?.length) {

    el.setAttribute(

      'data-mention-payload',

      JSON.stringify({ knowledgeItems }),

    );

  } else if (skillPayload) {

    el.setAttribute(

      'data-mention-payload',

      JSON.stringify({ skillPayload }),

    );

  }

  return el;

}



/**
 * 提交用：`@正文` / `/正文`（无触发符后空格），末尾无条件追加不可见边界符，
 * 使正文与后续用户正文的拼接天然无歧义（见 MENTION_BOUNDARY_MARKER 注释）
 */

export function serializeMentionBody(

  body: string,

  kind: TMentionEditorKind,

): string {

  const trimmed = body.trim();

  if (!trimmed) return '';

  const withTrigger = kind === 'knowledge' ? `@${trimmed}` : `/${trimmed}`;

  return `${withTrigger}${MENTION_BOUNDARY_MARKER}`;

}



/** 从旧版 label 串提取正文（仅插入路径兜底，不做历史反解析） */

export function parseMentionBodyFromLabel(

  label: string,

  kind: TMentionEditorKind,

): string {

  const trimmed = label.trim();

  const prefix = kind === 'knowledge' ? '@' : '/';

  if (trimmed.startsWith(prefix)) {

    return trimmed.slice(prefix.length).trim();

  }

  return trimmed;

}


